/**
 * Add-to-calendar links for the event.
 *
 * Two routes because no single one works everywhere: Google Calendar takes a
 * URL, while Apple Calendar and Outlook want an .ics file.
 *
 * The implementation lives here, in CommonJS, so the backend and the bundled
 * frontends share exactly one copy — same reason as greek.cjs. It first served
 * only the microsite, where the .ics is built in the browser; the ticket email
 * needs the identical invite and cannot run JavaScript, so the server builds it
 * too. Two implementations of an RFC 5545 body would drift, and the drift would
 * show up as an invite that imports on a phone but not in Outlook.
 *
 * Everything here is pure: no DOM, no Blob, no window. The browser-only
 * download lives with the component.
 */

/** '2026-11-14 08:30:00' → '20261114T083000' (local wall time, paired with TZID). */
function toCalendarStamp(value) {
  if (!value) return null;
  const [date, time = '00:00:00'] = String(value).split(' ');
  return `${date.replace(/-/g, '')}T${time.replace(/:/g, '')}`;
}

/** All-day fallback when the organisers have not set times yet. */
function allDayStamps(eventDate) {
  if (!eventDate) return null;
  const start = String(eventDate).slice(0, 10).replace(/-/g, '');
  const [y, m, d] = String(eventDate).slice(0, 10).split('-').map(Number);
  const next = new Date(Date.UTC(y, m - 1, d + 1));
  const end = next.toISOString().slice(0, 10).replace(/-/g, '');
  return { start, end };
}

/**
 * Timezones we can define in the file.
 *
 * A TZID is only legal if the same object defines it (RFC 5545 §3.6.5), and
 * Apple Calendar is the client that actually enforces it: given
 * `DTSTART;TZID=Europe/Athens` with no VTIMEZONE it refuses the invite, which
 * is what "add to Apple calendar doesn't work" turned out to be. Google infers
 * IANA names, which is why every other route looked fine.
 *
 * Only the zone this event runs in is defined here. Anything else falls back to
 * UTC stamps — see utcStamp — rather than naming a zone the file cannot back up.
 */
const VTIMEZONES = {
  'Europe/Athens': [
    'BEGIN:VTIMEZONE',
    'TZID:Europe/Athens',
    'X-LIC-LOCATION:Europe/Athens',
    'BEGIN:DAYLIGHT',
    'TZOFFSETFROM:+0200',
    'TZOFFSETTO:+0300',
    'TZNAME:EEST',
    'DTSTART:19700329T030000',
    'RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=-1SU',
    'END:DAYLIGHT',
    'BEGIN:STANDARD',
    'TZOFFSETFROM:+0300',
    'TZOFFSETTO:+0200',
    'TZNAME:EET',
    'DTSTART:19701025T040000',
    'RRULE:FREQ=YEARLY;BYMONTH=10;BYDAY=-1SU',
    'END:STANDARD',
    'END:VTIMEZONE',
  ],
};

/**
 * A wall-clock stamp in `timeZone` as a UTC stamp: '20261128T083000' in Athens
 * → '20261128T063000Z'.
 *
 * Used only for zones we cannot define. The instant is exact, so no client has
 * to be told what the zone means; the cost is that the entry no longer follows
 * the venue if the organisers later move the event's timezone.
 *
 * Solved by fixed-point rather than by an offset table: formatting the guess in
 * the target zone says how far off it is, and one correction lands it — a
 * second pass settles the hour where a DST change falls between the two.
 */
function utcStamp(stamp, timeZone) {
  const n = (from, to) => Number(stamp.slice(from, to));
  // The wall clock read as if it were UTC. Fixed: every pass measures the
  // zone's offset at its current guess and applies it to *this*, never to the
  // previous answer — correcting cumulatively subtracts the offset twice and
  // lands three hours early in an Athens summer.
  const wall = Date.UTC(n(0, 4), n(4, 6) - 1, n(6, 8), n(9, 11), n(11, 13), n(13, 15) || 0);

  const offsetAt = (instant) => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(new Date(instant));
    const part = (type) => Number(parts.find((p) => p.type === type).value);
    // hour can come back as 24 for midnight in some ICU versions.
    const asZone = Date.UTC(part('year'), part('month') - 1, part('day'), part('hour') % 24, part('minute'), part('second'));
    return asZone - instant;
  };

  // Second pass settles the hour when a DST change falls between the guess and
  // the answer.
  let utc = wall - offsetAt(wall);
  utc = wall - offsetAt(utc);

  return `${new Date(utc).toISOString().replace(/[-:]/g, '').split('.')[0]}Z`;
}

/**
 * Fold a content line to 75 octets, as RFC 5545 §3.1 requires.
 *
 * Measured in UTF-8 bytes, not characters: Greek costs two bytes apiece, so a
 * description that looks short is well past the limit — the live invite had a
 * 78-octet line. Never splits inside a character, and each continuation begins
 * with the single space that marks it as a continuation.
 */
function foldLine(line) {
  const encoder = new TextEncoder();
  if (encoder.encode(line).length <= 75) return line;

  const folded = [];
  let current = '';
  let bytes = 0;

  for (const char of line) {
    const size = encoder.encode(char).length;
    if (bytes + size > 75) {
      folded.push(current);
      // The leading space counts towards the next line's 75.
      current = ` ${char}`;
      bytes = 1 + size;
    } else {
      current += char;
      bytes += size;
    }
  }
  folded.push(current);

  return folded.join('\r\n');
}

function buildIcs({ title, description, location, startsAt, endsAt, eventDate, timezone, url, uid }) {
  const escape = (s) =>
    String(s || '')
      .replace(/\\/g, '\\\\')
      .replace(/;/g, '\\;')
      .replace(/,/g, '\\,')
      .replace(/\r?\n/g, '\\n');

  const zone = timezone || 'Europe/Athens';
  const zoneDefinition = VTIMEZONES[zone];

  const start = toCalendarStamp(startsAt);
  const end = toCalendarStamp(endsAt);
  const timed = Boolean(start && end);

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Hellenic Bank Association//Monday Festival//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
  ];

  // The definition belongs to the calendar, not the event, and must precede
  // the component that references it.
  if (timed && zoneDefinition) lines.push(...zoneDefinition);

  lines.push(
    'BEGIN:VEVENT',
    /*
     * The UID is the identity of the entry, and calendar clients dedupe on it:
     * two invites sharing one UID means the second silently replaces the first.
     * Per-session and per-meeting invites must therefore pass their own — the
     * date alone is the *event's* identity, and letting three bookmarked
     * sessions inherit it leaves the attendee with exactly one of them.
     */
    `UID:${uid || `hba-event-${eventDate || 'tbc'}`}@themondayfestival.gr`,
    // DTSTAMP must be UTC; the event times stay local with a TZID.
    `DTSTAMP:${new Date().toISOString().replace(/[-:]/g, '').split('.')[0]}Z`
  );

  if (timed) {
    if (zoneDefinition) {
      lines.push(`DTSTART;TZID=${zone}:${start}`);
      lines.push(`DTEND;TZID=${zone}:${end}`);
    } else {
      lines.push(`DTSTART:${utcStamp(start, zone)}`);
      lines.push(`DTEND:${utcStamp(end, zone)}`);
    }
  } else {
    const allDay = allDayStamps(eventDate);
    if (!allDay) return null;
    // A date-only entry names no zone, so it needs no definition.
    lines.push(`DTSTART;VALUE=DATE:${allDay.start}`);
    lines.push(`DTEND;VALUE=DATE:${allDay.end}`);
  }

  lines.push(
    `SUMMARY:${escape(title)}`,
    `DESCRIPTION:${escape(description)}`,
    `LOCATION:${escape(location)}`,
    url ? `URL:${escape(url)}` : null,
    // A day-before reminder, which is the whole point of offering this.
    'BEGIN:VALARM',
    'TRIGGER:-P1D',
    'ACTION:DISPLAY',
    `DESCRIPTION:${escape(title)}`,
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR'
  );

  // RFC 5545 wants CRLF line endings and a terminating one after the last
  // line; Outlook minds the first, Apple minds both.
  return `${lines.filter(Boolean).map(foldLine).join('\r\n')}\r\n`;
}

function googleCalendarUrl({ title, description, location, startsAt, endsAt, eventDate, timezone }) {
  const url = new URL('https://calendar.google.com/calendar/render');
  url.searchParams.set('action', 'TEMPLATE');
  url.searchParams.set('text', title || '');
  if (description) url.searchParams.set('details', description);
  if (location) url.searchParams.set('location', location);
  url.searchParams.set('ctz', timezone || 'Europe/Athens');

  const start = toCalendarStamp(startsAt);
  const end = toCalendarStamp(endsAt);
  if (start && end) {
    url.searchParams.set('dates', `${start}/${end}`);
  } else {
    const allDay = allDayStamps(eventDate);
    if (allDay) url.searchParams.set('dates', `${allDay.start}/${allDay.end}`);
  }
  return url.toString();
}

/**
 * Venue and address are separate CMS fields but often hold the same value early
 * on; de-duplicate so the invite does not read "Αθήνα, Αθήνα".
 */
function calendarLocation(venue, address) {
  return [...new Set([venue, address].filter(Boolean))].join(', ');
}

module.exports = {
  toCalendarStamp,
  allDayStamps,
  buildIcs,
  googleCalendarUrl,
  calendarLocation,
  foldLine,
  utcStamp,
};
